import { Col, Row } from '@opendoor/atoms/box';
import { Input } from '@opendoor/atoms/input';
import { Link } from '@opendoor/atoms/link';
import { Radio } from '@opendoor/atoms/radio';
import { Text } from '@opendoor/atoms/text';
import { IconCoin, IconInfoCircle } from '@tabler/icons-react';
import { DatePicker } from 'components/buyer/buyDirectCheckout/shared/DatePicker';
import { isLoanFinancing } from 'components/offerPortal/offerSubmission';
import {
  isCloseDateValid,
  isMoneyAskValid,
  isPeriodValid,
  type OfferPortalWizardContext,
  PERIOD_LIMIT_DAYS,
} from 'state_machines/OfferPortalWizardMachine';

import {
  isAllowedCloseDate,
  isFhaEligible,
  type ListingDetails,
  minCloseDaysFromToday,
} from './listingDetails';
import {
  CardHeading,
  cardTitle,
  CONTINGENT_STATUS_OPTIONS,
  FIELD_CARD,
  FIELD_INPUT,
  FieldError,
  fieldLabel,
  FINANCING_OPTIONS,
  LENDER_OPTIONS,
  PERIOD_FIELDS,
  RADIO_GROUP_AS_CARD_TITLE,
  RADIO_GROUP_AS_FIELD,
  RADIO_ROW,
  type Send,
} from './wizardFields';

/** Step 2 of 6 of the offer wizard — financing, close date, and contingency. */
const DetailsStep = ({
  listing,
  context,
  send,
}: {
  listing: ListingDetails;
  context: OfferPortalWizardContext;
  send: Send;
}) => {
  const handleDownPaymentChange = (value: number | null) => {
    send({ type: 'SET_DOWN_PAYMENT_CENTS', value: value != null ? Math.round(value * 100) : null });
  };

  const wholeDays = (next: number | null): number | null =>
    next != null ? Math.round(next) : null;

  // One handler per period field, so the rendered input takes a named
  // reference rather than a closure built during render.
  const periodHandlers: Record<
    (typeof PERIOD_FIELDS)[number]['field'],
    (next: number | null) => void
  > = {
    inspectionPeriodDays: (next) => {
      send({ type: 'SET_INSPECTION_PERIOD_DAYS', value: wholeDays(next) });
    },
    appraisalPeriodDays: (next) => {
      send({ type: 'SET_APPRAISAL_PERIOD_DAYS', value: wholeDays(next) });
    },
    financingPeriodDays: (next) => {
      send({ type: 'SET_FINANCING_PERIOD_DAYS', value: wholeDays(next) });
    },
  };

  const today = new Date();
  const fhaEligible = isFhaEligible(listing, today);
  const minCloseDays = minCloseDaysFromToday(listing, today);
  // Legacy disabled the FHA option outright during the waiting period; the
  // design keeps it selectable with the notice, so the restriction moves to
  // the close date — an FHA offer cannot close before the home is eligible.
  const fhaMinDate =
    context.financingType === 'fha' && !fhaEligible ? (listing.fha_eligible_date ?? null) : null;
  const closeDateOk = isCloseDateValid(context);
  const closeDateViolation = context.fieldViolations['closeOfEscrow'];
  const closeDateInvalid = (context.showError && !closeDateOk) || closeDateViolation != null;
  const statusOk = !context.contingentOnSale || context.contingentHomeStatus != null;
  const fhaUntil =
    listing.fha_eligible_date == null
      ? ''
      : new Intl.DateTimeFormat('en-US', {
          month: '2-digit',
          day: '2-digit',
          year: '2-digit',
        }).format(new Date(`${listing.fha_eligible_date}T00:00:00`));
  const closeDateError = () => {
    if (!context.showError || closeDateOk) {
      return undefined;
    }
    if (fhaMinDate != null && context.closeDate != null && context.closeDate < fhaMinDate) {
      return `FHA offers on this home can’t close before ${fhaUntil}. Pick a later date or change the financing type.`;
    }
    // The picker blocks unsupported days, so the failure here is an empty
    // field (or a stale draft) — the design words both as too-soon.
    if (minCloseDays != null && minCloseDays > 0) {
      return `Closing date cannot be shorter than ${minCloseDays} days from today.`;
    }
    return 'Select the closing date.';
  };
  // Empty string, never undefined: Base UI fixes controlled-ness on the first
  // render, so a group that starts undefined and later holds an answer flips
  // from uncontrolled to controlled and throws.
  const contingentValue = () => {
    if (context.contingentOnSale == null) return '';
    return context.contingentOnSale ? 'yes' : 'no';
  };
  // Only the closing days the home supports are pickable — and none before
  // the eligibility date while FHA is the financing type.
  const isDayOutOfRange = (day: Date): boolean =>
    !isAllowedCloseDate(listing, day, new Date()) ||
    (fhaMinDate != null && day < new Date(`${fhaMinDate}T00:00:00`));
  return (
    <>
      <Col className={FIELD_CARD}>
        <Radio.Group
          className={RADIO_GROUP_AS_CARD_TITLE}
          label={
            <Text as="span" className={cardTitle()}>
              How is your client paying?
            </Text>
          }
          value={context.financingType ?? ''}
          onValueChange={(value) => send({ type: 'SET_FINANCING_TYPE', value: String(value) })}
          error={
            context.showError && !context.financingType
              ? 'Select how your client is paying.'
              : undefined
          }
        >
          {FINANCING_OPTIONS.map((option) => (
            <Radio.Item
              key={option.value}
              label={option.label}
              value={option.value}
              className={RADIO_ROW}
            />
          ))}
        </Radio.Group>
        {context.fieldViolations['financingType'] != null ? (
          <FieldError>{context.fieldViolations['financingType']}</FieldError>
        ) : null}
        {/* Opendoor Home Loans offers conventional mortgages only, so the
            savings banner must not show for FHA or VA. */}
        {context.financingType === 'conventional' ? (
          <Row className="items-center gap-4 rounded-[12px] bg-od-bg-success/10 p-4">
            <IconCoin className="size-6 shrink-0 text-od-content-success" />
            <Text className="text-body-sm text-od-content-success">
              Opendoor Home Loans saves your buyer up to $21,600.
              <br />
              No origination points, no additional fees.{' '}
              <Link
                href="/home-loans"
                analyticsName="cosmos-make-offer-details-home-loans"
                className="text-od-content-success"
              >
                Learn more
              </Link>
            </Text>
          </Row>
        ) : null}
      </Col>
      {isLoanFinancing(context.financingType) ? (
        <Col className={FIELD_CARD}>
          <CardHeading
            title="Loan details"
            caption="The down payment and the lender your client is working with."
          />
          <Col className="gap-2">
            <Text
              as="label"
              htmlFor="down-payment"
              className={fieldLabel(
                (context.showError && context.downPaymentCents == null) ||
                  context.fieldViolations['downPaymentCents'] != null,
              )}
            >
              Down payment
            </Text>
            <Input
              id="down-payment"
              type="currency"
              decimals={0}
              min={0}
              value={context.downPaymentCents != null ? context.downPaymentCents / 100 : null}
              onValueChange={handleDownPaymentChange}
              placeholder="$"
              size="xs"
              className={FIELD_INPUT}
              aria-label="Down payment"
              error={context.showError && context.downPaymentCents == null}
              analyticsName="cosmos-make-offer-details-down-payment"
            />
            {context.fieldViolations['downPaymentCents'] != null ? (
              <FieldError>{context.fieldViolations['downPaymentCents']}</FieldError>
            ) : null}
            {!isMoneyAskValid(context.downPaymentCents, context.priceCents) ? (
              <FieldError>Down payment can’t exceed the offer amount.</FieldError>
            ) : null}
            {context.showError && context.downPaymentCents == null ? (
              <FieldError>Down payment cannot be empty for loan financing</FieldError>
            ) : null}
          </Col>
          <Radio.Group
            className={RADIO_GROUP_AS_FIELD}
            label={
              <Text as="span" className={fieldLabel(false)}>
                Who is the lender?
              </Text>
            }
            value={context.lenderType ?? ''}
            onValueChange={(value) => send({ type: 'SET_LENDER_TYPE', value: String(value) })}
            error={
              context.showError && context.lenderType == null ? 'Select the lender.' : undefined
            }
          >
            {LENDER_OPTIONS.map((option) => (
              <Radio.Item
                key={option.value}
                label={option.label}
                value={option.value}
                className={RADIO_ROW}
              />
            ))}
          </Radio.Group>
          {context.fieldViolations['lenderType'] != null ? (
            <FieldError>{context.fieldViolations['lenderType']}</FieldError>
          ) : null}
        </Col>
      ) : null}
      <Col className={FIELD_CARD}>
        <Text id="close-date-label" className={cardTitle(closeDateInvalid)}>
          Closing date
        </Text>
        <DatePicker
          compactCalendar
          id="close-date"
          date={context.closeDate != null ? new Date(`${context.closeDate}T00:00:00`) : undefined}
          onSelect={(date) =>
            send({ type: 'SET_CLOSE_DATE', value: date.toISOString().slice(0, 10) })
          }
          onClear={() => send({ type: 'CLEAR_CLOSE_DATE' })}
          isOutOfRange={isDayOutOfRange}
          placeholder="MM/DD/YY"
          ariaLabelledBy="close-date-label"
          fullWidthTrailingIcon
          error={closeDateInvalid}
        />
        {closeDateViolation != null ? <FieldError>{closeDateViolation}</FieldError> : null}
        {closeDateError() ? <FieldError>{closeDateError()}</FieldError> : null}
        {context.financingType === 'fha' && !fhaEligible && fhaUntil !== '' ? (
          <Row className="items-start gap-3 rounded-[12px] bg-od-bg-accent-brand/10 p-4">
            <IconInfoCircle className="size-5 shrink-0 text-od-content-accent-brand" />
            <Text className="text-body-sm text-od-content-secondary">
              <Text as="span" className="font-medium text-od-content-primary">
                This home isn’t FHA-eligible until {fhaUntil}.
              </Text>{' '}
              FHA requires a waiting period after a home’s purchase. Need to close sooner? Choose a
              different financing type.
            </Text>
          </Row>
        ) : null}
      </Col>
      <Col className={FIELD_CARD}>
        <CardHeading
          title="Contingency periods"
          caption="The days each contingency runs from acceptance."
        />
        {PERIOD_FIELDS.map((period) => {
          const value = context[period.contextKey];
          const violation = context.fieldViolations[period.field];
          const invalid =
            (context.showError && !isPeriodValid(value, period.field)) || violation != null;
          return (
            <Col key={period.field} className="gap-2">
              <Text as="label" htmlFor={period.id} className={fieldLabel(invalid)}>
                {period.label}
              </Text>
              <Input
                id={period.id}
                type="number"
                decimals={0}
                min={0}
                max={PERIOD_LIMIT_DAYS[period.field]}
                value={value}
                onValueChange={periodHandlers[period.field]}
                placeholder="0"
                size="xs"
                className={FIELD_INPUT}
                aria-label={period.label}
                aria-invalid={invalid}
                error={invalid}
                analyticsName={`cosmos-make-offer-details-${period.id}`}
              />
              {violation != null ? <FieldError>{violation}</FieldError> : null}
              {context.showError && !isPeriodValid(value, period.field) ? (
                <FieldError>Enter 0 to {PERIOD_LIMIT_DAYS[period.field]} days.</FieldError>
              ) : null}
            </Col>
          );
        })}
      </Col>
      <Col className={FIELD_CARD}>
        <Radio.Group
          className={RADIO_GROUP_AS_CARD_TITLE}
          label={
            <Text as="span" className={cardTitle()}>
              Is this offer contingent on the sale of another home?
            </Text>
          }
          value={contingentValue()}
          onValueChange={(value) =>
            send({ type: 'SET_CONTINGENT_ON_SALE', value: value === 'yes' })
          }
          error={
            context.showError && context.contingentOnSale == null ? 'Select yes or no.' : undefined
          }
        >
          <Radio.Item label="Yes" value="yes" className={RADIO_ROW} />
          <Radio.Item label="No" value="no" className={RADIO_ROW} />
        </Radio.Group>
        {context.contingentOnSale ? (
          <Radio.Group
            className={RADIO_GROUP_AS_CARD_TITLE}
            label={
              <Text as="span" className={cardTitle()}>
                What is the status of the contingent home?
              </Text>
            }
            value={context.contingentHomeStatus ?? ''}
            onValueChange={(value) =>
              send({ type: 'SET_CONTINGENT_HOME_STATUS', value: String(value) })
            }
            error={
              context.showError && !statusOk
                ? 'Select the status of the contingent home.'
                : undefined
            }
          >
            {CONTINGENT_STATUS_OPTIONS.map((option) => (
              <Radio.Item
                key={option.value}
                label={option.label}
                value={option.value}
                className={RADIO_ROW}
              />
            ))}
          </Radio.Group>
        ) : null}
        {context.fieldViolations['buyerHomeSaleStatus'] != null ? (
          <FieldError>{context.fieldViolations['buyerHomeSaleStatus']}</FieldError>
        ) : null}
      </Col>
    </>
  );
};

export default DetailsStep;
